import { ActionIcon, Menu } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAntenna,
  IconBrandGoogleDrive,
  IconCoin,
  IconDotsVertical,
  IconEye,
  IconFileSpreadsheet,
  IconPaperclip,
  IconPrinter,
  IconReceipt,
  IconTrash,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { Shipment } from "../../api/types";

export function ShipmentRowMenu({
  shipment,
  canEdit,
  isAdmin,
  onOpen,
  onOpenDocs,
  onChanged,
}: {
  shipment: Shipment;
  canEdit: boolean;
  isAdmin: boolean;
  onOpen: () => void;
  onOpenDocs: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();

  async function run(fn: () => Promise<unknown>, okMsg?: string) {
    try {
      await fn();
      if (okMsg) notifications.show({ color: "green", message: okMsg });
      onChanged();
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? e.message : String(e) });
    }
  }

  return (
    <Menu shadow="md" position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()} title={t("shipments.rowMenu.title")}>
          <IconDotsVertical size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
        <Menu.Item leftSection={<IconEye size={14} />} onClick={onOpen}>{t("shipments.rowMenu.open")}</Menu.Item>

        {canEdit && <Menu.Divider />}
        {canEdit && (shipment.chirpstack_added_at
          ? (isAdmin && (
              <Menu.Item leftSection={<IconAntenna size={14} />} onClick={() => void run(() => api.undoChirpstackDone(shipment.id))}>
                {t("shipments.chirpstackUndo")} (ChirpStack)
              </Menu.Item>
            ))
          : (
              <Menu.Item leftSection={<IconAntenna size={14} />} onClick={() => void run(() => api.markChirpstackDone(shipment.id), t("shipments.chirpstackMarked"))}>
                {t("shipments.markChirpstackDone")}
              </Menu.Item>
            ))}
        {canEdit && (shipment.accounting_confirmed_at
          ? (isAdmin && (
              <Menu.Item leftSection={<IconReceipt size={14} />} onClick={() => void run(() => api.unconfirmAccounting(shipment.id))}>
                {t("shipments.accountingUndo")} (Contabilitate)
              </Menu.Item>
            ))
          : (
              <Menu.Item leftSection={<IconReceipt size={14} />} onClick={() => void run(() => api.confirmAccounting(shipment.id), t("shipments.accountingMarked"))}>
                {t("shipments.accountingConfirm")}
              </Menu.Item>
            ))}
        {canEdit && (
          <Menu.Item leftSection={<IconCoin size={14} />} onClick={() => void run(() => api.markShipmentsDebt([shipment.id], !shipment.on_debt), t("shipments.debtToggled"))}>
            {shipment.on_debt ? t("shipments.unmarkDebt") : t("shipments.markDebtBtn")}
          </Menu.Item>
        )}

        <Menu.Divider />
        <Menu.Item leftSection={<IconFileSpreadsheet size={14} />} component="a" href={api.exportShipmentUrl(shipment.id)}>
          {t("shipments.rowMenu.exportXlsx")}
        </Menu.Item>
        <Menu.Item leftSection={<IconFileSpreadsheet size={14} />} component="a" href={api.exportShipmentUrl(shipment.id, { accounting: true })}>
          {t("shipments.rowMenu.exportXlsxAccounting")}
        </Menu.Item>
        {canEdit && (
          <Menu.Item leftSection={<IconBrandGoogleDrive size={14} />} onClick={() => void run(() => api.uploadShipmentToDrive(shipment.id), t("shipments.rowMenu.driveUploaded"))}>
            {t("shipments.rowMenu.uploadDrive")}
          </Menu.Item>
        )}
        <Menu.Item leftSection={<IconPaperclip size={14} />} onClick={onOpenDocs}>{t("shipments.docs.openDocs")}</Menu.Item>
        {canEdit && (
          <Menu.Item leftSection={<IconPrinter size={14} />} onClick={() => void run(() => api.godexPrintShipment(shipment.id), t("shipments.rowMenu.printSent"))}>
            {t("shipments.rowMenu.print")}
          </Menu.Item>
        )}

        {isAdmin && (
          <>
            <Menu.Divider />
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() => {
                if (window.confirm(t("shipments.rowMenu.confirmDelete", { code: shipment.code }))) {
                  void run(() => api.deleteShipment(shipment.id));
                }
              }}
            >
              {t("shipments.rowMenu.delete")}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
